import { FC, useState, useMemo } from "react";
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Checkbox,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@capacit-com/lara-law-shared-ui";
import { X, Plus, ChevronDown, ChevronUp, Database, AlertCircle } from "lucide-react";
import { DynamicListFilters } from "@/models/dashboard/WidgetConfig";
import useLegislationFilters from "@/hooks/useLegislationFilters";
import useWorkspace from "@/hooks/useWorkspace";
import { useDocumentMetadata } from "@/hooks/useWidgetData";

interface FilterBuilderProps {
  dataSource: "sources" | "workspace-documents";
  filters: DynamicListFilters;
  onChange: (filters: DynamicListFilters) => void;
}

interface FilterType {
  key: string;
  label: string;
  type: string;
}

// ===== Datasource Filter Card Component =====
interface DatasourceFilterCardProps {
  sourceName: string;
  displayName: string;
  sourceFilters: Record<string, string[]>;
  filterConfig: Record<string, { displayName?: string; options?: string[]; type?: string }>;
  onFiltersChange: (filters: Record<string, string[]>) => void;
  onRemove: () => void;
}

const DatasourceFilterCard: FC<DatasourceFilterCardProps> = ({
  sourceName,
  displayName,
  sourceFilters,
  filterConfig,
  onFiltersChange,
  onRemove,
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const [newFilterKey, setNewFilterKey] = useState<string>("");
  const [newFilterValue, setNewFilterValue] = useState<string>("");

  const filterTypes = useMemo((): FilterType[] => {
    return Object.entries(filterConfig).map(([key, config]) => ({
      key,
      label: config.displayName || key,
      type: config.type?.toLowerCase() || "multi",
    }));
  }, [filterConfig]);

  const getFilterOptions = (key: string): string[] => {
    return filterConfig[key]?.options || [];
  };

  const addFilter = () => {
    if (!newFilterKey || !newFilterValue) return;
    const currentValues = sourceFilters[newFilterKey] || [];
    if (!currentValues.includes(newFilterValue)) {
      onFiltersChange({
        ...sourceFilters,
        [newFilterKey]: [...currentValues, newFilterValue],
      });
    }
    setNewFilterValue("");
  };

  const removeFilterValue = (key: string, value: string) => {
    const currentValues = sourceFilters[key] || [];
    const newValues = currentValues.filter((v) => v !== value);
    if (newValues.length === 0) {
      const { [key]: _, ...rest } = sourceFilters;
      onFiltersChange(rest);
    } else {
      onFiltersChange({ ...sourceFilters, [key]: newValues });
    }
  };

  const activeFilters: { key: string; value: string; label: string }[] = [];
  Object.entries(sourceFilters).forEach(([key, values]) => {
    if (Array.isArray(values)) {
      const filterType = filterTypes.find((f) => f.key === key);
      values.forEach((value) => {
        activeFilters.push({ key, value, label: filterType?.label || key });
      });
    }
  });

  const availableOptions = newFilterKey ? getFilterOptions(newFilterKey) : [];

  return (
    <Card className="border">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CardHeader className="p-3 pb-0">
          <CollapsibleTrigger asChild>
            <div className="flex items-center justify-between cursor-pointer">
              <div className="flex items-center gap-2">
                <Database className="h-4 w-4 text-muted-foreground" />
                <CardTitle className="text-sm font-medium">{displayName}</CardTitle>
                {activeFilters.length > 0 && (
                  <Badge variant="secondary" className="text-xs">
                    {activeFilters.length} filter{activeFilters.length !== 1 ? "e" : ""}
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemove();
                  }}
                >
                  <X className="h-3 w-3" />
                </Button>
                {isOpen ? (
                  <ChevronUp className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                )}
              </div>
            </div>
          </CollapsibleTrigger>
        </CardHeader>

        <CollapsibleContent>
          <CardContent className="p-3 pt-2 space-y-3">
            {activeFilters.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {activeFilters.map((filter, index) => (
                  <Badge
                    key={`${sourceName}-${filter.key}-${filter.value}-${index}`}
                    variant="outline"
                    className="flex items-center gap-1 text-xs"
                  >
                    <span className="text-muted-foreground">{filter.label}:</span>
                    {filter.value}
                    <button
                      onClick={() => removeFilterValue(filter.key, filter.value)}
                      className="ml-0.5 hover:bg-muted rounded"
                    >
                      <X className="h-2.5 w-2.5" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}

            {filterTypes.length > 0 ? (
              <div className="flex items-end gap-2">
                <div className="flex-1 space-y-1">
                  <Label className="text-xs">Filter</Label>
                  <Select
                    value={newFilterKey}
                    onValueChange={(value) => {
                      setNewFilterKey(value);
                      setNewFilterValue("");
                    }}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="Vælg filter" />
                    </SelectTrigger>
                    <SelectContent>
                      {filterTypes.map((type) => (
                        <SelectItem key={type.key} value={type.key}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex-1 space-y-1">
                  <Label className="text-xs">Værdi</Label>
                  {availableOptions.length > 0 ? (
                    <Select value={newFilterValue} onValueChange={setNewFilterValue}>
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue placeholder="Vælg værdi" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableOptions.map((option) => (
                          <SelectItem key={option} value={option}>
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      value={newFilterValue}
                      onChange={(e) => setNewFilterValue(e.target.value)}
                      placeholder="Indtast værdi"
                      disabled={!newFilterKey}
                      className="h-8 text-xs"
                    />
                  )}
                </div>

                <Button
                  size="icon"
                  className="h-8 w-8"
                  onClick={addFilter}
                  disabled={!newFilterKey || !newFilterValue}
                >
                  <Plus className="h-3 w-3" />
                </Button>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground text-center py-2">
                Ingen filtre tilgængelige for denne kilde.
              </p>
            )}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};

// ===== Main FilterBuilder Component =====
const FilterBuilder: FC<FilterBuilderProps> = ({
  dataSource,
  filters,
  onChange,
}) => {
  const { useGetFilterConfiguration } = useLegislationFilters();
  const { useQueryAllWorkspaces } = useWorkspace();
  const { data: filterConfig, isLoading: isLoadingConfig } = useGetFilterConfiguration();
  const { data: workspaces } = useQueryAllWorkspaces();
  const [newDatasource, setNewDatasource] = useState<string>("");
  const [newMetadataKey, setNewMetadataKey] = useState<string>("");
  const [newMetadataValue, setNewMetadataValue] = useState<string>("");
  const [workspaceSearch, setWorkspaceSearch] = useState<string>("");

  const selectedWorkspaceIds = filters.workspaceIds ?? [];

  // Fetch document-level metadata fields and their actual values from the API,
  // scoped to the currently selected workspaces.
  const { data: discoveredMetadata } = useDocumentMetadata(
    dataSource === "workspace-documents" && selectedWorkspaceIds.length > 0
      ? selectedWorkspaceIds
      : dataSource === "workspace-documents"
      ? undefined
      : undefined
  );

  const selectedDatasources = useMemo(() => filters.datasource || [], [filters.datasource]);
  const sourceFilters = filters.sourceFilters || {};

  const availableDatasources = useMemo(() => {
    if (!filterConfig?.sources) return [];
    return Object.entries(filterConfig.sources)
      .filter(([name]) => !selectedDatasources.includes(name))
      .map(([name, config]) => ({
        name,
        displayName: config.displayName || name,
      }))
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [filterConfig, selectedDatasources]);

  // Build metadata field list from discovered values (from actual documents).
  // Falls back gracefully when the discovery endpoint hasn't loaded.
  const metadataFields = useMemo(() => {
    return (discoveredMetadata || []).map((f) => f.fieldName);
  }, [discoveredMetadata]);

  const metadataFilters = useMemo(
    () => filters.metadataFilters || {},
    [filters.metadataFilters]
  );

  // A filter badge is "stale" when discoveredMetadata has loaded but its field/value
  // no longer exists in the current workspace scope (e.g. the workspace was deselected).
  const staleFilterKeys = useMemo(() => {
    if (!discoveredMetadata) return new Set<string>();
    const stale = new Set<string>();
    for (const [key, values] of Object.entries(metadataFilters)) {
      const field = discoveredMetadata.find((f) => f.fieldName === key);
      if (!field) {
        values.forEach((v) => stale.add(`${key}:${v}`));
      } else {
        values.forEach((v) => {
          if (!field.distinctValues.includes(v)) stale.add(`${key}:${v}`);
        });
      }
    }
    return stale;
  }, [discoveredMetadata, metadataFilters]);

  const getMetadataOptions = (key: string): string[] => {
    const field = discoveredMetadata?.find((f) => f.fieldName === key);
    return field?.distinctValues ?? [];
  };

  const addMetadataFilter = () => {
    if (!newMetadataKey || !newMetadataValue) return;
    const existingValues = metadataFilters[newMetadataKey] || [];
    if (existingValues.includes(newMetadataValue)) return;
    onChange({
      ...filters,
      metadataFilters: {
        ...metadataFilters,
        [newMetadataKey]: [...existingValues, newMetadataValue],
      },
    });
    setNewMetadataValue("");
  };

  const removeMetadataFilter = (key: string, value: string) => {
    const existingValues = metadataFilters[key] || [];
    const nextValues = existingValues.filter((v) => v !== value);
    if (nextValues.length === 0) {
      const { [key]: _, ...remaining } = metadataFilters;
      onChange({
        ...filters,
        metadataFilters: Object.keys(remaining).length > 0 ? remaining : undefined,
      });
      return;
    }
    onChange({ ...filters, metadataFilters: { ...metadataFilters, [key]: nextValues } });
  };

  const toggleWorkspace = (workspaceId: string) => {
    const next = selectedWorkspaceIds.includes(workspaceId)
      ? selectedWorkspaceIds.filter((id) => id !== workspaceId)
      : [...selectedWorkspaceIds, workspaceId];
    onChange({
      ...filters,
      workspaceIds: next.length > 0 ? next : undefined,
    });
  };

  const clearWorkspaceFilters = () => {
    const { workspaceIds: _, ...rest } = filters;
    onChange(rest);
  };

  const metadataOptions = newMetadataKey ? getMetadataOptions(newMetadataKey) : [];

  const filteredWorkspaces = useMemo(() => {
    const all = workspaces || [];
    if (!workspaceSearch.trim()) return all;
    const q = workspaceSearch.toLowerCase();
    return all.filter((ws) => ws.name.toLowerCase().includes(q));
  }, [workspaces, workspaceSearch]);

  if (dataSource === "workspace-documents") {
    return (
      <div className="space-y-4">
        {/* Multi-workspace selection */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>Workspaces</Label>
            {selectedWorkspaceIds.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearWorkspaceFilters}
                className="text-xs h-6"
              >
                Ryd alle
              </Button>
            )}
          </div>
          {selectedWorkspaceIds.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {selectedWorkspaceIds.map((id) => {
                const ws = workspaces?.find((w) => w.id === id);
                return (
                  <Badge
                    key={id}
                    variant="outline"
                    className="flex items-center gap-1 text-xs"
                  >
                    {ws?.name ?? id}
                    <button
                      onClick={() => toggleWorkspace(id)}
                      className="ml-0.5 hover:bg-muted rounded"
                    >
                      <X className="h-2.5 w-2.5" />
                    </button>
                  </Badge>
                );
              })}
            </div>
          )}
          <Input
            placeholder="Søg workspaces..."
            value={workspaceSearch}
            onChange={(e) => setWorkspaceSearch(e.target.value)}
            className="h-8 text-xs"
          />
          <div className="border rounded-md max-h-40 overflow-y-auto p-1 space-y-0.5">
            {(workspaces || []).length === 0 && (
              <p className="text-xs text-muted-foreground p-2">Ingen workspaces tilgængelige</p>
            )}
            {filteredWorkspaces.length === 0 && (workspaces || []).length > 0 && (
              <p className="text-xs text-muted-foreground p-2">Ingen workspaces matcher søgningen</p>
            )}
            {filteredWorkspaces.map((workspace) => (
              <label
                key={workspace.id}
                className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted cursor-pointer text-sm"
              >
                <Checkbox
                  checked={selectedWorkspaceIds.includes(workspace.id)}
                  onCheckedChange={() => toggleWorkspace(workspace.id)}
                  id={`ws-${workspace.id}`}
                />
                <span className="truncate">{workspace.name}</span>
              </label>
            ))}
          </div>
          {selectedWorkspaceIds.length === 0 && (
            <p className="text-xs text-muted-foreground">Alle workspaces er inkluderet</p>
          )}
        </div>

        {/* Metadata filters */}
        <div className="space-y-2">
          <Label>Metadata filtre</Label>

          {Object.entries(metadataFilters).length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(metadataFilters).flatMap(([key, values]) =>
                values.map((value, index) => {
                  const isStale = staleFilterKeys.has(`${key}:${value}`);
                  return (
                    <Tooltip key={`${key}-${value}-${index}`}>
                      <TooltipTrigger asChild>
                        <Badge
                          variant="outline"
                          className={`flex items-center gap-1 text-xs ${isStale ? "border-destructive/60 text-destructive" : ""}`}
                        >
                          {isStale && <AlertCircle className="h-3 w-3 shrink-0" />}
                          <span className={isStale ? "" : "text-muted-foreground"}>{key}:</span>
                          {value}
                          <button
                            onClick={() => removeMetadataFilter(key, value)}
                            className="ml-0.5 hover:bg-muted rounded"
                          >
                            <X className="h-2.5 w-2.5" />
                          </button>
                        </Badge>
                      </TooltipTrigger>
                      {isStale && (
                        <TooltipContent>
                          Denne filterværdi findes ikke i de valgte workspaces
                        </TooltipContent>
                      )}
                    </Tooltip>
                  );
                })
              )}
            </div>
          )}

          <div className="flex items-end gap-2">
            <div className="flex-1 space-y-1">
              <Label className="text-xs">Felt</Label>
              <Select
                value={newMetadataKey}
                onValueChange={(value) => {
                  setNewMetadataKey(value);
                  setNewMetadataValue("");
                }}
                disabled={metadataFields.length === 0}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue
                    placeholder={
                      metadataFields.length === 0
                        ? "Ingen felter fundet"
                        : "Vælg metadata felt"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {metadataFields.map((field) => (
                    <SelectItem key={field} value={field}>
                      {field}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex-1 space-y-1">
              <Label className="text-xs">Værdi</Label>
              {metadataOptions.length > 0 ? (
                <Select value={newMetadataValue} onValueChange={setNewMetadataValue}>
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue placeholder="Vælg værdi" />
                  </SelectTrigger>
                  <SelectContent>
                    {metadataOptions.map((option) => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  value={newMetadataValue}
                  onChange={(e) => setNewMetadataValue(e.target.value)}
                  placeholder="Indtast værdi"
                  disabled={!newMetadataKey}
                  className="h-8 text-xs"
                />
              )}
            </div>

            <Button
              size="icon"
              className="h-8 w-8"
              onClick={addMetadataFilter}
              disabled={!newMetadataKey || !newMetadataValue}
            >
              <Plus className="h-3 w-3" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ===== Sources (legislation) filter section =====

  const getDatasourceDisplayName = (sourceName: string): string => {
    if (!filterConfig?.sources?.[sourceName]) return sourceName;
    return filterConfig.sources[sourceName].displayName || sourceName;
  };

  const addDatasource = () => {
    if (!newDatasource) return;
    onChange({
      ...filters,
      datasource: [...selectedDatasources, newDatasource],
      sourceFilters: { ...sourceFilters, [newDatasource]: {} },
    });
    setNewDatasource("");
  };

  const removeDatasource = (sourceName: string) => {
    const newDatasources = selectedDatasources.filter((d) => d !== sourceName);
    const { [sourceName]: _, ...remainingSourceFilters } = sourceFilters;
    if (newDatasources.length === 0) {
      onChange({});
    } else {
      onChange({ ...filters, datasource: newDatasources, sourceFilters: remainingSourceFilters });
    }
  };

  const updateSourceFilters = (sourceName: string, newSourceFilters: Record<string, string[]>) => {
    onChange({ ...filters, sourceFilters: { ...sourceFilters, [sourceName]: newSourceFilters } });
  };

  const clearAllFilters = () => {
    onChange({});
  };

  const totalFilterCount = Object.values(sourceFilters).reduce(
    (sum, sf) => sum + Object.values(sf).reduce((s, v) => s + (Array.isArray(v) ? v.length : 0), 0),
    0
  );

  return (
    <div className="space-y-4">
      {selectedDatasources.length > 0 && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Label className="text-sm text-muted-foreground">
              {selectedDatasources.length} kilde{selectedDatasources.length !== 1 ? "r" : ""} valgt
            </Label>
            {totalFilterCount > 0 && (
              <Badge variant="secondary" className="text-xs">
                {totalFilterCount} filter{totalFilterCount !== 1 ? "e" : ""}
              </Badge>
            )}
          </div>
          <Button variant="ghost" size="sm" onClick={clearAllFilters} className="text-xs h-6">
            Ryd alle
          </Button>
        </div>
      )}

      <div className="flex items-end gap-2">
        <div className="flex-1 space-y-1">
          <Label className="text-xs">Tilføj kilde</Label>
          <Select
            value={newDatasource}
            onValueChange={setNewDatasource}
            disabled={isLoadingConfig || availableDatasources.length === 0}
          >
            <SelectTrigger>
              <SelectValue
                placeholder={
                  isLoadingConfig
                    ? "Indlæser kilder..."
                    : availableDatasources.length === 0
                    ? "Alle kilder valgt"
                    : "Vælg kilde"
                }
              />
            </SelectTrigger>
            <SelectContent>
              {availableDatasources.map((source) => (
                <SelectItem key={source.name} value={source.name}>
                  {source.displayName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Button onClick={addDatasource} disabled={!newDatasource}>
          <Plus className="h-4 w-4 mr-1" />
          Tilføj
        </Button>
      </div>

      {selectedDatasources.length > 0 && (
        <div className="space-y-2">
          {selectedDatasources.map((sourceName) => {
            const sourceConfig = filterConfig?.sources?.[sourceName];
            return (
              <DatasourceFilterCard
                key={sourceName}
                sourceName={sourceName}
                displayName={getDatasourceDisplayName(sourceName)}
                sourceFilters={sourceFilters[sourceName] || {}}
                filterConfig={sourceConfig?.filters || {}}
                onFiltersChange={(newFilters) => updateSourceFilters(sourceName, newFilters)}
                onRemove={() => removeDatasource(sourceName)}
              />
            );
          })}
        </div>
      )}

      {selectedDatasources.length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-4">
          Tilføj en eller flere kilder for at konfigurere filtre.
        </p>
      )}
    </div>
  );
};

export default FilterBuilder;
